import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Image, Tag, Video } from '@frontend/core';
import { EMPTY, Observable, of, throwError, timer } from 'rxjs';
import { catchError, expand, filter, map, switchMap, take } from 'rxjs/operators';
import { ErrorService } from '../error';
import { NewsItem, NewsPage, NewsQuery, NewsRelation, NewsSubject, NewsTranslationState } from './news.model';

const JSON_API_MEDIA_TYPE = 'application/vnd.api+json';

const ALL_RELATIONS: NewsRelation[] = ['topics', 'tags', 'cultures', 'images', 'videos'];

type ResourceIdentifier = { type: string; id: string };

type ResourceObject = ResourceIdentifier & {
  attributes: Record<string, any>;
  relationships?: Record<string, { data: ResourceIdentifier[] | ResourceIdentifier | null }>;
  meta?: Record<string, any>;
};

type NewsDocument = { data: ResourceObject; included?: ResourceObject[] };

type NewsCollectionDocument = {
  data: ResourceObject[];
  included?: ResourceObject[];
  links: NewsPage['links'];
  meta: NewsPage['meta'];
};

/**
 * Reads published news from `/api/v1/news` and normalises the JSON:API
 * documents into `NewsItem`s. See the backend's `docs/api/frontend-news-api.md`.
 *
 * The language comes from `Accept-Language`, which the language interceptor
 * sets on every request; each item's `requestedLocale` and `servedLocale` say
 * what was asked for and what arrived.
 */
@Injectable({
  providedIn: 'root',
})
export class NewsService {
  private http = inject(HttpClient);
  private errorService = inject(ErrorService);

  /** Where the backend names no interval of its own. */
  private static readonly POLL_INTERVAL_SECONDS = 5;

  /** Backing off without a ceiling turns a slow translation into a poll that arrives after the reader has left. */
  private static readonly POLL_MAXIMUM_INTERVAL_SECONDS = 30;

  /**
   * Giving up is not a failure: the translation carries on and the reader sees
   * it on their next visit. This bounds how long a page keeps a timer running.
   */
  private static readonly POLL_MAXIMUM_ATTEMPTS = 12;

  private readonly headers = new HttpHeaders({ Accept: JSON_API_MEDIA_TYPE });

  getNews(query: NewsQuery): Observable<NewsPage> {
    return this.http
      .get<NewsCollectionDocument>('api/v1/news', { headers: this.headers, params: NewsService.toParams(query) })
      .pipe(
        map((document) => ({
          items: document.data.map((resource) => this.normalise(resource, document.included ?? [])),
          links: document.links,
          meta: document.meta,
        })),
        catchError((error) => this.handleError(error)),
      );
  }

  /** One item with everything it relates to, as its page shows it. */
  getNewsItem(slug: string): Observable<NewsItem> {
    return this.http
      .get<NewsDocument>('api/v1/news/' + encodeURIComponent(slug), {
        headers: this.headers,
        params: new HttpParams().set('include', ALL_RELATIONS.join(',')),
      })
      .pipe(
        map((document) => this.normalise(document.data, document.included ?? [])),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * The slug of the item with this database id, or `null` if it is not
   * published. A news notification's target carries the id; the item's page
   * is found by slug.
   */
  findSlug(id: string): Observable<string | null> {
    return this.getNews({ filter: { id: [id] }, pageSize: 1 }).pipe(map((page) => page.items[0]?.slug ?? null));
  }

  /**
   * Ask for the item in `locale`. The backend claims each item and locale
   * once, so asking again while a translation waits queues nothing. Answers
   * with the item in that locale as far as it exists, and where its
   * translation stands.
   */
  requestTranslation(slug: string, locale: string): Observable<NewsItem> {
    return this.http
      .post<NewsDocument>(
        'api/v1/news/' + encodeURIComponent(slug) + '/translations/' + encodeURIComponent(locale),
        null,
        { headers: this.headers },
      )
      .pipe(
        map((document) => this.normalise(document.data, document.included ?? [])),
        catchError((error) => this.handleError(error)),
      );
  }

  /**
   * Ask for the item until its translation stops being worth waiting for, then
   * emit it once. Emits nothing if it never settles within the attempts
   * allowed, and nothing if a poll fails: the page already shows the English.
   */
  watchTranslation(slug: string): Observable<NewsItem> {
    let attempts = 0;

    return timer(this.intervalFor(1)).pipe(
      switchMap(() => this.poll(slug)),
      expand((item) => {
        attempts++;

        if (item === null || !item.worthPolling || attempts >= NewsService.POLL_MAXIMUM_ATTEMPTS) {
          return EMPTY;
        }

        return timer(this.intervalFor(attempts + 1)).pipe(switchMap(() => this.poll(slug)));
      }),
      filter((item): item is NewsItem => item !== null && !item.worthPolling),
      take(1),
    );
  }

  /** Whether a page showing this item should ask for its translation. */
  static needsTranslation(item: NewsItem): boolean {
    return item.servedLocale !== item.requestedLocale && (item.translationState === 'absent' || item.translationState === 'failed');
  }

  static toParams(query: NewsQuery): HttpParams {
    let params = new HttpParams();
    const f = query.filter;

    for (const key of ['id', 'application', 'topic', 'culture', 'tag'] as const) {
      if (f[key]?.length) {
        params = params.set(`filter[${key}]`, f[key].join(','));
      }
    }

    if (f.featured !== undefined) {
      params = params.set('filter[featured]', f.featured ? 'true' : 'false');
    }

    if (f.search && f.search.trim().length >= 3) {
      params = params.set('filter[search]', f.search.trim());
    }

    if (query.sort) {
      params = params.set('sort', query.sort);
    }

    if (query.page) {
      params = params.set('page[number]', String(query.page));
    }

    if (query.pageSize) {
      params = params.set('page[size]', String(query.pageSize));
    }

    if (query.include?.length) {
      params = params.set('include', query.include.join(','));
    }

    return params;
  }

  private poll(slug: string): Observable<NewsItem | null> {
    return this.getNewsItem(slug).pipe(catchError(() => of(null)));
  }

  private intervalFor(attempt: number): number {
    return Math.min(NewsService.POLL_INTERVAL_SECONDS * attempt, NewsService.POLL_MAXIMUM_INTERVAL_SECONDS) * 1000;
  }

  private normalise(resource: ResourceObject, included: ResourceObject[]): NewsItem {
    const a = resource.attributes;
    const related = (relation: NewsRelation): ResourceObject[] => {
      const linkage = resource.relationships?.[relation]?.data;
      const identifiers = Array.isArray(linkage) ? linkage : linkage ? [linkage] : [];

      return identifiers
        .map((identifier) => included.find((r) => r.type === identifier.type && r.id === identifier.id))
        .filter((r): r is ResourceObject => !!r);
    };

    return {
      id: resource.id,
      slug: a['slug'],
      title: a['title'] ?? '',
      shortTitle: a['short_title'] ?? null,
      summary: a['summary'] ?? null,
      headline: a['headline'] ?? null,
      body: a['body'] ?? null,
      byline: a['byline'] ?? null,
      publishedAt: a['published_at'] ? new Date(a['published_at']) : null,
      publicUpdatedAt: a['public_updated_at'] ? new Date(a['public_updated_at']) : null,
      featured: !!a['featured'],
      platformWide: !!a['platform_wide'],
      topics: related('topics').map((r) => this.toSubject(r)),
      cultures: related('cultures').map((r) => this.toSubject(r)),
      tags: related('tags').map((r) => new Tag(+r.id, r.attributes['name'], r.attributes['slug'], r.attributes['tag_type'], null, null, null)),
      images: related('images').map((r) => this.toImage(r)),
      fallbackImage: a['fallback_image']?.path
        ? {
            id: 0,
            disk: a['fallback_image']['disk'],
            path: a['fallback_image']['path'],
            width: a['fallback_image']['width'] ?? null,
            height: a['fallback_image']['height'] ?? null,
            alt_text: a['fallback_image']['alt_text'] ?? null,
            role: 'thumbnail',
          }
        : null,
      videos: related('videos').map(
        (r) =>
          new Video(r.attributes['name'], +r.id, r.attributes['host_identifier'], r.attributes['host_params'], r.attributes['duration'], r.attributes['host']),
      ),
      requestedLocale: resource.meta?.['requested_locale'] ?? '',
      servedLocale: resource.meta?.['served_locale'] ?? '',
      translationState: (resource.meta?.['translation_state'] as NewsTranslationState) ?? null,
      worthPolling: !!resource.meta?.['worth_polling'],
    };
  }

  private toSubject(resource: ResourceObject): NewsSubject {
    return { id: +resource.id, name: resource.attributes['name'], slug: resource.attributes['slug'] };
  }

  private toImage(resource: ResourceObject): Image {
    const a = resource.attributes;

    return {
      id: +resource.id,
      disk: a['disk'],
      path: a['path'],
      width: a['width'] ?? null,
      height: a['height'] ?? null,
      alt_text: a['alt_text'] ?? null,
      title: a['title'] ?? null,
      caption: a['caption'] ?? null,
      credits: a['credits'] ?? null,
      licence: a['licence'] ?? null,
      role: a['role'] ?? undefined,
    };
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(() => this.errorService.normaliseHttpError(error, 'error.something_went_wrong'));
  }
}
